import { ExternalLink, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import SectionCard from './SectionCard';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetPersons } from '@/api/queries/useGetPersons';
import { Person } from '@/api/types/backend-dto-types';
import { Skeleton } from '@/components/atoms/Skeleton';

const KeyEntitiesContent = ({ entities, caseId }: { entities?: Person[]; caseId: string | null }) => {
  const navigate = useNavigate();

  if (!caseId || !entities || entities?.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <Users className="h-6 w-6 text-gray-500" />
        <span className="mb-6 w-1/2 text-center text-xs font-medium text-gray-500">
          We couldn't find any key entities for this case.
        </span>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      {entities.map((entity) => (
        <button
          key={entity.id}
          className="flex h-fit items-center gap-2 rounded-lg border p-1 pl-2 hover:bg-blue-50"
          onClick={() => {
            trackCustomEvent(TrackingEvents.ClickKeyEntityFromHome);
            navigate(`/app/chronos/case-editor/data/facts?caseId=${caseId}&personId=${entity.id}`);
          }}
        >
          <Users className="h-4 w-4 flex-shrink-0 text-gray-500" />
          <h3 className="truncate text-xs font-medium text-gray-700">{entity.name}</h3>
        </button>
      ))}
    </div>
  );
};

const HeaderActions = ({ caseId, numberOfEntities }: { caseId: string | null; numberOfEntities: number }) => {
  const navigate = useNavigate();

  if (!caseId || numberOfEntities === 0) {
    return null;
  }

  return (
    <button
      className="inline-flex items-center gap-1 text-sm text-blue-500 underline hover:text-blue-700"
      onClick={() => {
        trackCustomEvent(TrackingEvents.ViewAllEntitiesOnSummary);
        navigate(`/app/chronos/case-editor/data/persons?caseId=${caseId}`);
      }}
    >
      View All ({numberOfEntities})
      <ExternalLink className="h-3 w-3" />
    </button>
  );
};

export const KeyEntitiesSection = ({ caseId }: { caseId: string | null }) => {
  const { data: persons, isLoading: personsIsLoading } = useGetPersons({
    caseId,
    paginated: true,
    pageSize: 6,
  });

  return (
    <div className="lg:col-span-3">
      <SectionCard
        title="Key Entities"
        className="h-full"
        headerActions={<HeaderActions caseId={caseId} numberOfEntities={persons?.totalCount || 0} />}
      >
        {personsIsLoading ? (
          <div className="grid grid-cols-2 gap-2">
            <Skeleton className="h-3 w-full bg-gray-200" />
            <Skeleton className="h-3 w-full bg-gray-200" />
            <Skeleton className="h-3 w-full bg-gray-200" />
            <Skeleton className="h-3 w-full bg-gray-200" />
            <Skeleton className="h-3 w-full bg-gray-200" />
            <Skeleton className="h-3 w-full bg-gray-200" />
            <Skeleton className="h-3 w-full bg-gray-200" />
            <Skeleton className="h-3 w-full bg-gray-200" />
          </div>
        ) : (
          <KeyEntitiesContent entities={persons?.persons} caseId={caseId} />
        )}
      </SectionCard>
    </div>
  );
};
